import React, { useState } from 'react'
import AsyncSelect from 'react-select/async'
import { useIntl } from 'react-intl'
import { useMountEffect } from '../../../hooks'
import FieldRowWrapper from '../FieldRowWrapper/FieldRowWrapper'
import FieldWrapper from '../FieldRowWrapper'
import Typography from '@mui/material/Typography'
import RawHtml from 'stories/components/RawHtml'
import useStyles from './style'
import './dropdown.css'

const Dropdown = ({
  slug,
  label = '',
  name,
  required,
  touched,
  error,
  description,
  placeholder,
  labelKey = 'title',
  valueKey = 'slug',
  value,
  isClearable,
  customNoOptionsMessage,
  customEmptyListMessage,
  styles,
  disabled,
  getOptions,
  customized,
  alwaysEnableLabel,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const [hasFocus, setFocus] = useState(false)
  const [defaultOptions, setDefaultOptions] = useState([])
  const [currentOptions, setCurrentOptions] = useState([])
  const classes = useStyles()

  useMountEffect(() => {
    promiseOptions().then((res) => setDefaultOptions(res))
  })

  function formattedOptions(options = []) {
    // format list
    return options.map((option) => ({
      value: option.value || option[valueKey],
      label: option.label || option[labelKey],
    }))
  }

  const onInputFocus = () => {
    setFocus(true)
    props.onFocus && props.onFocus()
  }

  const onInputBlur = () => {
    setFocus(false)
    props.onBlur && props.onBlur()
  }

  const onInputChange = (option) => {
    if (!option) {
      props.onChange('')
      return
    }
    props.onChange(option.value || option)
  }

  const promiseOptions = async (params = {}) => {
    const [err, res] = await getOptions(slug, params)
    if (res && !err) {
      setCurrentOptions(formattedOptions(res.objects))
      return Promise.resolve(formattedOptions(res.objects))
    }
    return Promise.reject()
  }

  const renderLabel = () => {
    if (alwaysEnableLabel) return true
    if (Array.isArray(value) && value.length === 0) return false
    if (value) return true
    return false
  }

  const classNames = {
    'formz-dropdown-input': true,
    'formz-has-focus': hasFocus,
    'formz-has-error': error,
    'full-width': props.fullWidth,
  }

  const renderValue = () => {
    if (value && typeof value === 'object') {
      return currentOptions.find(
        (op) => op.value === (value.value || value[valueKey])
      )
    }
    if (value && typeof value === 'string') {
      return currentOptions.find((op) => op.value === value)
    }
    return value
  }

  const SelectProps = {
    name,
    value: renderValue(),
    autosize: false,
    placeholder:
      placeholder || formatMessage({ id: 'form.fields.dropDown.placeholder' }),
    onBlur: onInputBlur,
    onFocus: onInputFocus,
    onChange: onInputChange,
    error,
    isClearable,
    disabled,
    noOptionsMessage: () =>
      formatMessage({ id: customEmptyListMessage || 'general.dropdown.empty' }),
  }

  if (customized)
    return (
      <FieldRowWrapper
        className={classNames}
        error={error}
        touched={touched ? touched.toString() : undefined}
      >
        <label className="formz-field-and-label-holder">
          <div className="formz-input-title">
            {label} {required && '(*)'}
          </div>

          <RawHtml className="formz-field-description" content={description} />
          <AsyncSelect
            className="Select"
            cacheOptions
            defaultOptions={defaultOptions}
            loadOptions={(inputVal) => promiseOptions({ search: inputVal })}
            {...SelectProps}
          />
        </label>
      </FieldRowWrapper>
    )

  return (
    <FieldWrapper
      error={error}
      touched={touched ? touched.toString() : undefined}
      fullWidth={props.fullWidth}
    >
      <div className={classes.select}>
        {renderLabel() && label && (
          <Typography
            variant="caption"
            component="div"
            className={`${classes.label} select-label`}
          >
            {renderLabel() && label}
          </Typography>
        )}

        <AsyncSelect
          cacheOptions
          defaultOptions={defaultOptions}
          loadOptions={(inputVal) => promiseOptions({ search: inputVal })}
          classNamePrefix="Select"
          {...SelectProps}
        />
      </div>
    </FieldWrapper>
  )
}

class AsyncDropdown extends React.Component {
  render() {
    return <Dropdown {...this.props} />
  }
}
export default AsyncDropdown
